import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Box,
  Stack,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tabs,
  Tab,
  Alert,
  Switch,
  FormControlLabel,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SMART_COLORS as COLORS,
  smartActionButtonSx,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import api from '../../services/api';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import SmartTablePagination, { useSmartPagination } from '../../components/SmartTablePagination';

const emptyCaseTypeForm = {
  case_type_id: null,
  case_type_key: '',
  name_en: '',
  name_ar: '',
  description: '',
  sort_order: 100,
  is_active: 1,
};

const optionConfig = {
  caseTypes: {
    titleKey: 'reproductionSettings.caseType',
    recordsLabelKey: 'reproductionSettings.caseTypes',
    keyField: 'case_type_key',
    idField: 'case_type_id',
    endpoint: '/reproduction-clinic/case-types',
  },
  caseStatuses: {
    titleKey: 'reproductionSettings.status',
    recordsLabelKey: 'reproductionSettings.caseStatuses',
    keyField: 'status_key',
    idField: 'status_id',
    endpoint: '/reproduction-clinic/case-statuses',
  },
  examTypes: {
    titleKey: 'reproductionSettings.examType',
    recordsLabelKey: 'reproductionSettings.examTypes',
    keyField: 'exam_type_key',
    idField: 'exam_type_id',
    endpoint: '/reproduction-clinic/exam-types',
  },
};

function MetricCard({ icon, label, value, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 104 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: '50%',
            bgcolor: soft,
            color,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color, fontWeight: 900 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900, overflowWrap: 'anywhere' }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

const ReproductionProceduresList = () => {
  const { t, i18n } = useTranslation();
  const [procedures, setProcedures] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);
  const [caseStatuses, setCaseStatuses] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [tab, setTab] = useState('procedures');
  const [caseTypeDialog, setCaseTypeDialog] = useState(false);
  const [caseTypeForm, setCaseTypeForm] = useState(emptyCaseTypeForm);
  const [optionKind, setOptionKind] = useState('caseTypes');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteProcedureId, setDeleteProcedureId] = useState(null);
  const [deleteOption, setDeleteOption] = useState(null);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const isRtl = String(i18n.language || '').startsWith('ar');

  const fetchProcedures = useCallback(async () => {
    const response = await api.get('/reproduction-clinic/procedures');
    setProcedures(Array.isArray(response.data) ? response.data : []);
  }, []);

  const setOptionRows = (kind, rows) => {
    if (kind === 'caseTypes') setCaseTypes(rows);
    if (kind === 'caseStatuses') setCaseStatuses(rows);
    if (kind === 'examTypes') setExamTypes(rows);
  };

  const getOptionRows = (kind) => {
    if (kind === 'caseStatuses') return caseStatuses;
    if (kind === 'examTypes') return examTypes;
    return caseTypes;
  };

  const fetchOptions = useCallback(async (kind) => {
    const config = optionConfig[kind];
    const response = await api.get(config.endpoint);
    setOptionRows(kind, Array.isArray(response.data) ? response.data : []);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([
        fetchProcedures(),
        fetchOptions('caseTypes'),
        fetchOptions('caseStatuses'),
        fetchOptions('examTypes'),
      ]);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || t('reproductionSettings.loadError'));
    } finally {
      setLoading(false);
    }
  }, [fetchOptions, fetchProcedures, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = useCallback(async () => {
    setError('');
    setMessage('');
    try {
      await api.delete(`/reproduction-clinic/procedures/${deleteProcedureId}`, {
        data: { user_id: localStorage.getItem('userId') },
      });
      setDeleteProcedureId(null);
      setMessage(t('reproductionSettings.deleteSuccess'));
      await fetchProcedures();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || t('reproductionSettings.deleteError'));
    }
  }, [deleteProcedureId, fetchProcedures, t]);

  const openCaseTypeDialog = (row = emptyCaseTypeForm, kind = tab === 'caseStatuses' || tab === 'examTypes' ? tab : 'caseTypes') => {
    const config = optionConfig[kind];
    setOptionKind(kind);
    setCaseTypeForm({
      ...emptyCaseTypeForm,
      ...row,
      case_type_id: row?.[config.idField] || null,
      case_type_key: row?.[config.keyField] || row?.case_type_key || '',
    });
    setCaseTypeDialog(true);
  };

  const saveCaseType = async () => {
    setError('');
    const config = optionConfig[optionKind];
    try {
      const payload = {
        ...caseTypeForm,
        [config.keyField]: caseTypeForm.case_type_key || caseTypeForm.name_en,
        is_active: caseTypeForm.is_active ? 1 : 0,
      };
      if (caseTypeForm.case_type_id) {
        await api.put(`${config.endpoint}/${caseTypeForm.case_type_id}`, payload);
      } else {
        await api.post(config.endpoint, payload);
      }
      setCaseTypeDialog(false);
      setCaseTypeForm(emptyCaseTypeForm);
      setMessage(t('reproductionSettings.optionSaved'));
      await fetchOptions(optionKind);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || t('reproductionSettings.optionSaveError'));
    }
  };

  const deleteCaseType = async (row, kind = 'caseTypes') => {
    const config = optionConfig[kind];
    try {
      await api.delete(`${config.endpoint}/${row[config.idField]}`, {
        data: { user_id: localStorage.getItem('userId') },
      });
      setMessage(t('reproductionSettings.optionDeleted'));
      setDeleteOption(null);
      fetchOptions(kind);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || t('reproductionSettings.optionDeleteError'));
    }
  };

  const activeOptionConfig = optionConfig[tab] || optionConfig.caseTypes;
  const activeRows = useMemo(() => {
    if (tab === 'procedures') return procedures;
    if (tab === 'caseStatuses') return caseStatuses;
    if (tab === 'examTypes') return examTypes;
    return caseTypes;
  }, [caseStatuses, caseTypes, examTypes, procedures, tab]);
  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return activeRows;
    return activeRows.filter((row) => (
      String(row.procedure_name || '').toLowerCase().includes(term)
      || String(row.description || '').toLowerCase().includes(term)
      || String(row.name_en || '').toLowerCase().includes(term)
      || String(row.name_ar || '').toLowerCase().includes(term)
      || String(row[activeOptionConfig.keyField] || '').toLowerCase().includes(term)
    ));
  }, [activeOptionConfig.keyField, activeRows, search]);
  const summary = useMemo(() => ({
    procedures: procedures.length,
    caseTypes: caseTypes.length,
    caseStatuses: caseStatuses.length,
    examTypes: examTypes.length,
  }), [caseStatuses.length, caseTypes.length, examTypes.length, procedures.length]);
  const {
    page,
    rowsPerPage,
    paginatedItems,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useSmartPagination(filteredRows, [tab, search]);

  return (
    <Box className="smart-page-container" dir={isRtl ? 'rtl' : 'ltr'} sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <SettingsPageHeader
        title={t('reproductionSettings.title', 'Reproduction clinic settings')}
        subtitle={t(
          'reproductionSettings.pageHint',
          'Manage production clinic procedures, case types, statuses, and exam types with Arabic and English labels.'
        )}
        count={tab === 'procedures' ? procedures.length : getOptionRows(tab).length}
        actions={tab === 'procedures' ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/AddReproductionProcedure')}
            sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}
          >
            {t('reproductionSettings.addProcedure', 'Add reproduction procedure')}
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openCaseTypeDialog(emptyCaseTypeForm, tab)}
            sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}
          >
            {t('reproductionSettings.addOption')} {t(activeOptionConfig.titleKey)}
          </Button>
        )}
      />
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard icon={<AddIcon />} label={t('reproductionSettings.procedures')} value={summary.procedures} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard icon={<AddIcon />} label={t('reproductionSettings.caseTypes')} value={summary.caseTypes} color={COLORS.blue} soft={COLORS.blueSoft} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard icon={<AddIcon />} label={t('reproductionSettings.caseStatuses')} value={summary.caseStatuses} color={COLORS.green} soft={COLORS.greenSoft} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard icon={<AddIcon />} label={t('reproductionSettings.examTypes')} value={summary.examTypes} color={COLORS.warning} soft={COLORS.warningSoft} />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ ...cardSx, mb: 2, overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ px: 1, borderBottom: `1px solid ${COLORS.line}` }} variant="scrollable" scrollButtons="auto">
          <Tab value="procedures" label={t('reproductionSettings.procedures', 'Procedures')} sx={{ fontWeight: 900 }} />
          <Tab value="caseTypes" label={t('reproductionSettings.caseTypes', 'Case Types / أنواع الحالات')} sx={{ fontWeight: 900 }} />
          <Tab value="caseStatuses" label={t('reproductionSettings.caseStatuses', 'Status / الحالات')} sx={{ fontWeight: 900 }} />
          <Tab value="examTypes" label={t('reproductionSettings.examTypes', 'Exam Types / أنواع الفحص')} sx={{ fontWeight: 900 }} />
        </Tabs>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ p: 2 }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            label={t('reproductionSettings.search')}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: COLORS.muted, mr: isRtl ? 0 : 1, ml: isRtl ? 1 : 0 }} /> }}
            sx={{ maxWidth: { sm: 420 } }}
            fullWidth
          />
        </Stack>
      </Paper>

        {tab === 'procedures' && (
        <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
          <TableContainer sx={smartTableContainerSx}>
            <Table size="small" sx={smartTableSx}>
              <TableHead sx={smartTableHeadSx}>
                <TableRow>
                  <TableCell align="center" sx={{ width: 72 }}>#</TableCell>
                  <TableCell>{t('procedure_name', 'Procedure Name')}</TableCell>
                  <TableCell>{t('description', 'Description')}</TableCell>
                  <TableCell align="center">{t('price', 'Price')}</TableCell>
                  <TableCell align="center" sx={{ width: 150 }}>{t('actions', 'Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length > 0 ? (
                  paginatedItems.map((procedure, index) => (
                    <TableRow key={procedure.procedure_id} hover>
                      <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ color: COLORS.text, fontWeight: 900, minWidth: 190 }}>{procedure.procedure_name}</TableCell>
                      <TableCell sx={{ minWidth: 240 }}>{procedure.description || '-'}</TableCell>
                      <TableCell align="center">{procedure.default_price ?? procedure.price ?? 0}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title={t('edit')}>
                            <IconButton onClick={() => navigate(`/EditReproductionProcedure/${procedure.procedure_id}`)} sx={smartActionButtonSx(COLORS.blue, COLORS.blueSoft)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('delete')}>
                            <IconButton onClick={() => setDeleteProcedureId(procedure.procedure_id)} sx={smartActionButtonSx(COLORS.rose, COLORS.roseSoft)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {search ? t('no_search_results') : t('reproductionSettings.noProcedures', 'No reproduction procedures available')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <SmartTablePagination count={filteredRows.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
        </Paper>
        )}
        {['caseTypes', 'caseStatuses', 'examTypes'].includes(tab) && (
          <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
            <TableContainer sx={smartTableContainerSx}>
              <Table size="small" sx={smartTableSx}>
                <TableHead sx={smartTableHeadSx}>
                  <TableRow>
                    <TableCell align="center" sx={{ width: 72 }}>#</TableCell>
                    <TableCell>{t('key', 'Key')}</TableCell>
                    <TableCell>{t('english_name', 'English Name')}</TableCell>
                    <TableCell>{t('arabic_name', 'Arabic Name')}</TableCell>
                    <TableCell>{t('description', 'Description')}</TableCell>
                    <TableCell align="center">{t('sort_order', 'Sort')}</TableCell>
                    <TableCell align="center">{t('status', 'Status')}</TableCell>
                    <TableCell align="center" sx={{ width: 150 }}>{t('actions', 'Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.length ? paginatedItems.map((row, index) => (
                    <TableRow key={row[activeOptionConfig.idField] || row[activeOptionConfig.keyField]} hover>
                      <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ minWidth: 160 }}>{row[activeOptionConfig.keyField]}</TableCell>
                      <TableCell sx={{ minWidth: 180 }}>{row.name_en}</TableCell>
                      <TableCell sx={{ minWidth: 180 }}>{row.name_ar}</TableCell>
                      <TableCell sx={{ minWidth: 220 }}>{row.description || '-'}</TableCell>
                      <TableCell align="center">{row.sort_order}</TableCell>
                      <TableCell align="center">
                        <Chip label={Number(row.is_active ?? 1) === 1 ? t('active', 'Active') : t('inactive', 'Inactive')} sx={{ bgcolor: Number(row.is_active ?? 1) === 1 ? COLORS.greenSoft : COLORS.cardSoft, fontWeight: 900 }} />
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title={t('edit')}>
                            <IconButton onClick={() => openCaseTypeDialog(row, tab)} sx={smartActionButtonSx(COLORS.blue, COLORS.blueSoft)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('delete')}>
                            <span>
                              <IconButton onClick={() => setDeleteOption({ row, kind: tab })} disabled={!row[activeOptionConfig.idField]} sx={smartActionButtonSx(COLORS.rose, COLORS.roseSoft)}>
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">{search ? t('no_search_results') : t('reproductionSettings.noOptions', 'No options available')}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <SmartTablePagination count={filteredRows.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
          </Paper>
        )}
      <Dialog open={caseTypeDialog} onClose={() => setCaseTypeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>
          {caseTypeForm.case_type_id ? t('reproductionSettings.editOption') : t('reproductionSettings.addOption')} {t(optionConfig[optionKind].titleKey)}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              {t('reproductionSettings.keyHint', 'The key is the value stored in the clinic record. English and Arabic names are what users see in the clinic.')}
            </Alert>
            <TextField label={t('stored_value', 'Key / Stored value')} value={caseTypeForm.case_type_key} onChange={(event) => setCaseTypeForm((value) => ({ ...value, case_type_key: event.target.value }))} fullWidth />
            <TextField required label={t('english_name', 'English name')} value={caseTypeForm.name_en} onChange={(event) => setCaseTypeForm((value) => ({ ...value, name_en: event.target.value }))} fullWidth />
            <TextField required label={t('arabic_name', 'Arabic name')} value={caseTypeForm.name_ar} onChange={(event) => setCaseTypeForm((value) => ({ ...value, name_ar: event.target.value }))} fullWidth />
            <TextField label={t('description', 'Description')} value={caseTypeForm.description || ''} onChange={(event) => setCaseTypeForm((value) => ({ ...value, description: event.target.value }))} fullWidth multiline rows={2} />
            <TextField type="number" label={t('sort_order', 'Sort order')} value={caseTypeForm.sort_order} onChange={(event) => setCaseTypeForm((value) => ({ ...value, sort_order: event.target.value }))} fullWidth />
            <FormControlLabel
              control={<Switch checked={Boolean(caseTypeForm.is_active)} onChange={(event) => setCaseTypeForm((value) => ({ ...value, is_active: event.target.checked ? 1 : 0 }))} />}
              label={t('active', 'Active')}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCaseTypeDialog(false)}>{t('cancel', 'Cancel')}</Button>
          <Button disabled={!caseTypeForm.name_en.trim() || !caseTypeForm.name_ar.trim()} onClick={saveCaseType} variant="contained" sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}>
            {t('save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(deleteProcedureId)} onClose={() => setDeleteProcedureId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{t('delete_confirmation')}</DialogTitle>
        <DialogContent>
          <Typography>{t('reproductionSettings.confirmDeleteProcedure')}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteProcedureId(null)}>{t('cancel')}</Button>
          <Button onClick={handleDelete} sx={{ color: COLORS.rose, fontWeight: 900 }}>{t('delete')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(deleteOption)} onClose={() => setDeleteOption(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{t('delete_confirmation')}</DialogTitle>
        <DialogContent>
          <Typography>{t('reproductionSettings.confirmDeactivate', { name: deleteOption?.row?.name_en || '' })}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOption(null)}>{t('cancel')}</Button>
          <Button onClick={() => deleteCaseType(deleteOption.row, deleteOption.kind)} sx={{ color: COLORS.rose, fontWeight: 900 }}>{t('delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReproductionProceduresList;
